// @owners { team: patients }
import { useEffect, useRef } from 'react';
// safe because this component is only used on web
// eslint-disable-next-line @alto/prefer-alto-navigation-library
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { useVersionPoller } from '~shared/hooks/useVersionPoller';
import { EVENTS } from '~shared/lib/analytics/src/constants';

export const AppVersionWatcher = () => {
  const { isStale, detectedVersion } = useVersionPoller();
  const location = useLocation();
  const { trackEvent } = useAnalytics();

  const hasReloadedRef = useRef(false);

  const handleAppReload = (trigger: 'route_change', onReload: () => void) => {
    if (hasReloadedRef.current) return;
    hasReloadedRef.current = true;
    trackEvent({
      event: EVENTS.STALE_APP_RELOADED,
      params: {
        trigger,
        running_version: process.env.WEB_VERSION,
        detected_version: detectedVersion,
      },
    });
    onReload();
  };

  // Route change trigger — skip the initial mount by tracking previous location
  const prevLocationRef = useRef<string | null>(null);

  // handleAppReload captures hook values via closure; read the latest via a ref so the route-change
  // effect only depends on location/isStale.
  const handleAppReloadRef = useRef(handleAppReload);
  useEffect(() => {
    handleAppReloadRef.current = handleAppReload;
  });

  useEffect(() => {
    const current = location.pathname + location.search;
    const prev = prevLocationRef.current;
    prevLocationRef.current = current;

    if (prev === null || !isStale || current === prev) return;

    handleAppReloadRef.current('route_change', () => {
      window.location.href = current;
    });
  }, [location, isStale]);

  return null;
};
